import React from "react";
import {
  CheckCircleIcon,
  GlobeAltIcon,
  HeartIcon,
  MapPinIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

import Hero from "../components/Hero";
import heroImg from "../assests/images/hero.jpg";
import aboutImg from "../assests/images/aboutUs.jpg";
import CTASection from "../components/CTASection";

import { Helmet } from "react-helmet-async";

export default function AboutUs() {
  const features = [
    {
      icon: UsersIcon,
      number: "01",
      title: "Trusted by Travelers",
      desc: "Travelers rely on Dubai Hotel Rates to discover comfortable stays and competitive hotel rates across Dubai.",
    },
    {
      icon: MapPinIcon,
      number: "02",
      title: "Great Locations",
      desc: "Explore hotels near Dubai's beaches, attractions, shopping destinations and major business districts.",
    },
    {
      icon: HeartIcon,
      number: "03",
      title: "Traveler First",
      desc: "We make finding the right hotel easier by keeping the search experience simple, clear and convenient.",
    },
    {
      icon: ShieldCheckIcon,
      number: "04",
      title: "Reliable & Convenient",
      desc: "Compare hotel options with confidence through a platform built around convenience and transparency.",
    },
  ];

  const stats = [
    {
      value: "500+",
      label: "Hotels",
    },
    {
      value: "10K+",
      label: "Travelers",
    },
    {
      value: "24/7",
      label: "Support",
    },
    {
      value: "25+",
      label: "Years Experience",
    },
  ];

  return (
    <>
      {/* =========================================================
                SEO
            ========================================================= */}
      <Helmet>
        <title>About Us | Dubai Hotel Rates</title>

        <meta
          name="description"
          content="Learn more about Dubai Hotel Rates, your trusted platform for discovering hotels, comparing rates and finding great stays across Dubai."
        />

        <link
          rel="canonical"
          href="https://marinadubai.com/about-us"
        />

        <meta
          property="og:title"
          content="About Us | Dubai Hotel Rates"
        />

        <meta
          property="og:description"
          content="Discover Dubai Hotel Rates and learn how we help travelers find better hotels, better rates and memorable stays in Dubai."
        />

        <meta
          property="og:url"
          content="https://marinadubai.com/about-us"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="About Us | Dubai Hotel Rates"
        />

        <meta
          name="twitter:description"
          content="Discover Dubai Hotel Rates and find comfortable stays and competitive hotel rates across Dubai."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>

      <div className="w-full overflow-hidden bg-white">

        {/* =====================================================
                    HERO
                ====================================================== */}
        <Hero
          variant="inner"
          title="About Dubai Hotel Rates"
          subtitle="Helping travelers discover better hotels, better rates, and memorable stays across Dubai."
          backgroundImage={heroImg}
        />


        {/* =====================================================
                    INTRO / STORY
                ====================================================== */}
        <section className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-28">

          {/* Decorative Elements */}
          <div
            className="
              pointer-events-none
              absolute
              -right-40
              top-10
              h-96
              w-96
              rounded-full
              bg-[#0B5552]/5
              blur-3xl
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -left-40
              bottom-0
              h-72
              w-72
              rounded-full
              bg-[#D6A84F]/5
              blur-3xl
            "
          />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">

              {/* =================================================
                                IMAGE AREA
                            ================================================= */}
              <div className="relative">

                <div className="relative ml-0 max-w-xl lg:ml-6">

                  {/* Image */}
                  <div className="relative overflow-hidden rounded-[32px]">

                    <img
                      src={aboutImg}
                      alt="Dubai Hotel Rates"
                      className="
                        h-[380px]
                        w-full
                        object-cover
                        transition-transform
                        duration-700
                        hover:scale-105
                        sm:h-[480px]
                        lg:h-[570px]
                      "
                    />

                    <div
                      className="
                        absolute
                        inset-0
                        bg-gradient-to-t
                        from-[#0B5552]/65
                        via-transparent
                        to-transparent
                      "
                    />
                  </div>


                  {/* Floating Experience Card */}
                  <div
                    className="
                      absolute
                      -bottom-7
                      left-5
                      rounded-[22px]
                      border
                      border-slate-100
                      bg-white
                      px-6
                      py-5
                      shadow-[0_20px_50px_rgba(15,23,42,0.18)]
                      sm:left-8
                      sm:px-7
                      sm:py-6
                    "
                  >
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                      Experience
                    </p>

                    <div className="mt-1 flex items-end gap-2">

                      <span className="text-3xl font-extrabold text-[#0B5552] sm:text-4xl">
                        25+
                      </span>

                      <span className="mb-1 text-sm font-medium text-slate-500">
                        Years
                      </span>

                    </div>
                  </div>


                  {/* Gold Corner */}
                  <div
                    className="
                      absolute
                      -left-5
                      -top-5
                      h-20
                      w-20
                      rounded-tl-[28px]
                      border-l-4
                      border-t-4
                      border-[#D6A84F]
                    "
                  />


                  {/* Small Floating Badge */}
                  <div
                    className="
                      absolute
                      right-4
                      top-4
                      flex
                      items-center
                      gap-2
                      rounded-full
                      border
                      border-white/20
                      bg-[#0B5552]/80
                      px-4
                      py-2
                      backdrop-blur-md
                    "
                  >
                    <SparklesIcon className="h-4 w-4 text-[#D6A84F]" />

                    <span className="text-xs font-bold text-white">
                      Dubai Hotels
                    </span>
                  </div>

                </div>

              </div>


              {/* =================================================
                                CONTENT
                            ================================================= */}
              <div>

                {/* Section Label */}
                <div className="mb-5 flex items-center gap-3">

                  <span className="h-[2px] w-10 bg-[#D6A84F]" />

                  <span
                    className="
                      text-xs
                      font-bold
                      uppercase
                      tracking-[0.2em]
                      text-[#0B5552]
                    "
                  >
                    Who We Are
                  </span>

                </div>


                {/* Heading */}
                <h2
                  className="
                    text-3xl
                    font-extrabold
                    leading-tight
                    tracking-tight
                    text-[#0F172A]
                    sm:text-4xl
                    lg:text-5xl
                  "
                >
                  Making Every Stay

                  <span className="block text-[#0B5552]">
                    Worth Remembering
                  </span>
                </h2>


                {/* Paragraph */}
                <p
                  className="
                    mt-6
                    text-sm
                    leading-7
                    text-slate-600
                    sm:text-base
                  "
                >
                  Dubai Hotel Rates was created with a simple goal:
                  helping travelers find the right hotel without making
                  the search complicated. Whether you are visiting Dubai
                  for business, relaxation, shopping, or a memorable
                  getaway, we help you discover accommodation that fits
                  your needs.
                </p>


                <p
                  className="
                    mt-4
                    text-sm
                    leading-7
                    text-slate-600
                    sm:text-base
                  "
                >
                  We bring together hotels, destinations and useful
                  travel information to make discovering Dubai stays
                  easier. Our focus is on convenience, value and helping
                  travelers make confident accommodation decisions.
                </p>


                {/* Highlights */}
                <div className="mt-8 space-y-4">

                  {/* Highlight 1 */}
                  <div className="flex items-start gap-4">

                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-[#E8F3F2]
                      "
                    >
                      <CheckCircleIcon className="h-5 w-5 text-[#0B5552]" />
                    </div>

                    <div>

                      <h3 className="text-sm font-bold text-[#0F172A]">
                        Carefully Selected Stays
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                        Explore accommodation selected around comfort,
                        location, quality and value.
                      </p>

                    </div>

                  </div>


                  {/* Highlight 2 */}
                  <div className="flex items-start gap-4">

                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-[#FBF5E7]
                      "
                    >
                      <CheckCircleIcon className="h-5 w-5 text-[#A47A2D]" />
                    </div>

                    <div>

                      <h3 className="text-sm font-bold text-[#0F172A]">
                        Value Focused
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                        Discover competitive hotel rates while keeping
                        comfort and convenience in mind.
                      </p>

                    </div>

                  </div>

                </div>


                {/* Stats */}
                <div
                  className="
                    mt-10
                    grid
                    grid-cols-2
                    gap-3
                    sm:grid-cols-4
                  "
                >

                  {stats.map((stat, index) => (
                    <div
                      key={index}
                      className="
                        rounded-2xl
                        border
                        border-slate-100
                        bg-[#F7FAF9]
                        px-4
                        py-4
                        transition
                        duration-300
                        hover:-translate-y-1
                        hover:border-[#0B5552]/20
                        hover:shadow-sm
                      "
                    >

                      <div className="text-xl font-extrabold text-[#0B5552] sm:text-2xl">
                        {stat.value}
                      </div>

                      <div className="mt-1 text-[10px] font-medium text-slate-500 sm:text-xs">
                        {stat.label}
                      </div>

                    </div>
                  ))}

                </div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
                    MISSION / VISION
                ====================================================== */}
        <section
          className="
            relative
            overflow-hidden
            bg-[#F4F9F8]
            py-16
            sm:py-20
            lg:py-24
          "
        >

          {/* Decorative */}
          <div
            className="
              pointer-events-none
              absolute
              -right-40
              top-10
              h-96
              w-96
              rounded-full
              bg-[#0B5552]/5
              blur-3xl
            "
          />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

            {/* Section Heading */}
            <div className="mx-auto mb-12 max-w-2xl text-center">

              <div className="mb-4 flex items-center justify-center gap-3">

                <span className="h-[2px] w-8 bg-[#D6A84F]" />

                <span
                  className="
                    text-xs
                    font-bold
                    uppercase
                    tracking-[0.2em]
                    text-[#0B5552]
                  "
                >
                  Our Direction
                </span>

                <span className="h-[2px] w-8 bg-[#D6A84F]" />

              </div>

              <h2
                className="
                  text-3xl
                  font-extrabold
                  text-[#0F172A]
                  sm:text-4xl
                "
              >
                Built Around Better Travel
              </h2>

              <p
                className="
                  mt-4
                  text-sm
                  leading-7
                  text-slate-500
                  sm:text-base
                "
              >
                Everything we do starts with one goal:
                creating a simpler and more enjoyable way
                to discover hotels and travel experiences in Dubai.
              </p>

            </div>


            {/* Mission / Vision */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

              {/* =================================================
                                MISSION
                            ================================================= */}
              <div
                className="
                  group
                  relative
                  min-h-[340px]
                  overflow-hidden
                  rounded-[30px]
                  bg-[#0B5552]
                  p-7
                  shadow-[0_20px_60px_rgba(11,85,82,0.18)]
                  sm:p-10
                "
              >

                {/* Glow */}
                <div
                  className="
                    pointer-events-none
                    absolute
                    -right-24
                    -top-24
                    h-72
                    w-72
                    rounded-full
                    bg-white/10
                    blur-3xl
                  "
                />

                <div
                  className="
                    pointer-events-none
                    absolute
                    -bottom-32
                    -left-20
                    h-64
                    w-64
                    rounded-full
                    bg-[#D6A84F]/10
                    blur-3xl
                  "
                />

                <div className="relative flex h-full flex-col justify-between">

                  <div>

                    {/* Icon */}
                    <div
                      className="
                        flex
                        h-14
                        w-14
                        items-center
                        justify-center
                        rounded-2xl
                        bg-[#D6A84F]
                        text-[#0B5552]
                      "
                    >
                      <SparklesIcon className="h-7 w-7" />
                    </div>


                    <div className="mt-8">

                      <p
                        className="
                          text-[10px]
                          font-bold
                          uppercase
                          tracking-[0.2em]
                          text-[#E8C879]
                        "
                      >
                        Our Mission
                      </p>

                      <h3
                        className="
                          mt-3
                          text-2xl
                          font-extrabold
                          text-white
                          sm:text-3xl
                        "
                      >
                        Simplifying the way you travel.
                      </h3>

                      <p
                        className="
                          mt-4
                          max-w-lg
                          text-sm
                          leading-7
                          text-white/70
                        "
                      >
                        To simplify the hotel discovery experience by
                        connecting travelers with great stays, competitive
                        rates and useful information they can trust.
                      </p>

                    </div>

                  </div>


                  <div
                    className="
                      mt-8
                      flex
                      items-center
                      gap-2
                      text-xs
                      font-bold
                      text-white/50
                    "
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-[#D6A84F]" />
                    Making hotel discovery simpler
                  </div>

                </div>

              </div>


              {/* =================================================
                                VISION
                            ================================================= */}
              <div
                className="
                  group
                  relative
                  min-h-[340px]
                  overflow-hidden
                  rounded-[30px]
                  border
                  border-slate-200
                  bg-white
                  p-7
                  shadow-sm
                  transition
                  duration-300
                  hover:border-[#0B5552]/20
                  hover:shadow-lg
                  sm:p-10
                "
              >

                <div
                  className="
                    pointer-events-none
                    absolute
                    -right-24
                    -top-24
                    h-72
                    w-72
                    rounded-full
                    bg-[#0B5552]/5
                    blur-3xl
                  "
                />

                <div className="relative flex h-full flex-col justify-between">

                  <div>

                    {/* Icon */}
                    <div
                      className="
                        flex
                        h-14
                        w-14
                        items-center
                        justify-center
                        rounded-2xl
                        bg-[#E8F3F2]
                        text-[#0B5552]
                      "
                    >
                      <GlobeAltIcon className="h-7 w-7" />
                    </div>


                    <div className="mt-8">

                      <p
                        className="
                          text-[10px]
                          font-bold
                          uppercase
                          tracking-[0.2em]
                          text-[#0B5552]
                        "
                      >
                        Our Vision
                      </p>

                      <h3
                        className="
                          mt-3
                          text-2xl
                          font-extrabold
                          text-[#0F172A]
                          sm:text-3xl
                        "
                      >
                        A better way to discover Dubai.
                      </h3>

                      <p
                        className="
                          mt-4
                          max-w-lg
                          text-sm
                          leading-7
                          text-slate-500
                        "
                      >
                        To become a trusted platform for hotel discovery
                        and travel inspiration, helping travelers find
                        memorable stays and make confident choices
                        throughout their Dubai journey.
                      </p>

                    </div>

                  </div>


                  <div
                    className="
                      mt-8
                      flex
                      items-center
                      gap-2
                      text-xs
                      font-bold
                      text-slate-400
                    "
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-[#0B5552]" />
                    Discover more with confidence
                  </div>

                </div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
                    WHY CHOOSE US
                ====================================================== */}
        <section
          className="
            relative
            overflow-hidden
            bg-white
            py-16
            sm:py-20
            lg:py-28
          "
        >

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

            {/* Heading */}
            <div
              className="
                mb-12
                flex
                flex-col
                gap-5
                lg:flex-row
                lg:items-end
                lg:justify-between
              "
            >

              <div className="max-w-2xl">

                <div className="mb-4 flex items-center gap-3">

                  <span className="h-[2px] w-10 bg-[#D6A84F]" />

                  <span
                    className="
                      text-xs
                      font-bold
                      uppercase
                      tracking-[0.2em]
                      text-[#0B5552]
                    "
                  >
                    Why Dubai Hotel Rates
                  </span>

                </div>

                <h2
                  className="
                    text-3xl
                    font-extrabold
                    tracking-tight
                    text-[#0F172A]
                    sm:text-4xl
                    lg:text-5xl
                  "
                >
                  Travel With

                  <span className="text-[#0B5552]">
                    {" "}Confidence
                  </span>
                </h2>

              </div>


              <p
                className="
                  max-w-md
                  text-sm
                  leading-7
                  text-slate-500
                  lg:text-right
                "
              >
                Everything we do is focused on making your hotel search
                easier, more transparent and more enjoyable.
              </p>

            </div>


            {/* Feature Cards */}
            <div
              className="
                grid
                grid-cols-1
                gap-4
                sm:grid-cols-2
                lg:grid-cols-4
              "
            >

              {features.map((item, index) => {

                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="
                      group
                      relative
                      overflow-hidden
                      rounded-[26px]
                      border
                      border-slate-200
                      bg-white
                      p-7
                      transition-all
                      duration-500
                      hover:-translate-y-2
                      hover:border-[#0B5552]/20
                      hover:shadow-[0_20px_50px_rgba(11,85,82,0.10)]
                    "
                  >

                    {/* Number */}
                    <span
                      className="
                        absolute
                        right-6
                        top-5
                        text-xs
                        font-extrabold
                        text-slate-100
                        transition-colors
                        duration-300
                        group-hover:text-[#E8F3F2]
                      "
                    >
                      {item.number}
                    </span>


                    {/* Icon */}
                    <div
                      className="
                        flex
                        h-14
                        w-14
                        items-center
                        justify-center
                        rounded-2xl
                        bg-[#E8F3F2]
                        text-[#0B5552]
                        transition-all
                        duration-300
                        group-hover:bg-[#0B5552]
                        group-hover:text-white
                      "
                    >
                      <Icon className="h-7 w-7" />
                    </div>


                    {/* Title */}
                    <h3
                      className="
                        mt-6
                        text-lg
                        font-bold
                        text-[#0F172A]
                      "
                    >
                      {item.title}
                    </h3>


                    {/* Description */}
                    <p
                      className="
                        mt-3
                        text-sm
                        leading-6
                        text-slate-500
                      "
                    >
                      {item.desc}
                    </p>


                    {/* Bottom Label */}
                    <div
                      className="
                        mt-6
                        flex
                        items-center
                        gap-2
                        text-xs
                        font-bold
                        text-[#0B5552]
                      "
                    >
                      <span
                        className="
                          h-1.5
                          w-1.5
                          rounded-full
                          bg-[#D6A84F]
                        "
                      />

                      Travel with confidence
                    </div>


                    {/* Hover Line */}
                    <div
                      className="
                        absolute
                        bottom-0
                        left-0
                        h-1
                        w-0
                        bg-gradient-to-r
                        from-[#0B5552]
                        to-[#D6A84F]
                        transition-all
                        duration-500
                        group-hover:w-full
                      "
                    />

                  </div>
                );
              })}

            </div>

          </div>
        </section>


        {/* =====================================================
                    FINAL CTA
                ====================================================== */}
        <CTASection />

      </div>
    </>
  );
}